import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AuthContext } from './context';
import { devUser, loadSession, logoutRequest, signInUrl, AUTH_PORTAL } from './session';
import type { AuthState, AuthStatus, SessionUser } from './types';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const dev = devUser();
    if (dev) {
      setUser(dev);
      setStatus('authenticated');
      return;
    }
    setStatus('loading');
    void loadSession().then((res) => {
      if (cancelled) return;
      if (res.kind === 'ok') {
        setUser(res.user);
        setStatus('authenticated');
      } else if (res.kind === 'unauthenticated') {
        setStatus('redirecting');
        window.location.assign(signInUrl());
      } else {
        setError(res.message);
        setStatus(res.kind);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const logout = useCallback(async () => {
    await logoutRequest();
    setUser(null);
    setStatus('redirecting');
    window.location.assign(`${AUTH_PORTAL}/login`);
  }, []);
  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  const value = useMemo<AuthState>(
    () => ({ status, user, error, logout, retry }),
    [status, user, error, logout, retry],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
